import {
  ContentLayout,
  Container,
  Header,
  ButtonDropdown,
  Input,
  StatusIndicator,
  TextFilter,
  Alert,
  PropertyFilterProps,
  Pagination,
} from '@cloudscape-design/components';
import BusinessUnitForm from 'src/components/businessUnit/BusinessUnitForm';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ExpandableTable, {
  cancelEditing,
  collapseALLView,
  Column,
  TableRow,
} from 'src/components/ExpandableTable/ExpandableTable';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { BusinessUnit, BusinessUnitWithDivisions } from 'src/types/orbit/BusinessUnit';
import { Division } from 'src/types/orbit/Division';
import useUserProfile from 'src/hooks/useUserProfile';
import { checkFeaturePermission, formatDateTime } from 'src/utils/generalUtils';
import { useDivision } from 'src/store/division/useDivision';
import DivisionForm from 'src/components/division/DivisionForm';
import FilterModal from 'src/components/filter/FilterModel';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import { ExportButton } from 'src/components/ExportButton';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import { Persona } from 'src/contstant/Persona';
import {
  getTeamsFilterProperties,
  getItemValue,
  getSearchableFields,
  initialFilters,
  exportBUConfig,
  convertToExportList,
} from './teamsConfig';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { usePropertyFilterPersistence } from 'src/hooks/usePropertyFilterPersistence';
import { useActivePropertyFilterCount } from 'src/hooks/useActivePropertyFilterCount';
import { getPageFilterKey } from 'src/contstant/storageKeys';

// Interface for expanded table data structure
interface ExpandTableData extends TableRow {
  businessUnitId: string;
  name: string;
  lastUpdatedAt: string;
  action?: React.ReactNode;
  status: React.ReactNode;
  expandedContent?: React.ReactNode;
}

const TeamsPage = () => {
  // Get user profile information
  const { getUserProgramId, getUserId, getPersona, getUserBusinessUnitId, getDivisions } =
    useUserProfile();
  const { userPermission, checkUserPermissions, currentUser } = useUsers();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  const [isDefaultExpendView, setDefaultExpandView] = useState(true);

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): any => {
    return {
      tokens: [],
      operation: 'and' as const,
      searchText: '',
    };
  }, []);

  // Use filter persistence hook
  const { filters: queryFilters, setFilters: setQueryFilters } = useFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'teams'),
    defaultFilters: getDefaultFilters(),
    excludeFields: ['businessUnitId'],
    businessUnitId,
  });

  // Count active filters
  const activeFilterCount = useActivePropertyFilterCount(queryFilters);

  const isEditDivision = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionConfig.DIVISION.EDIT_DIVISION,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  const isEditBusinessUnit = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionConfig.BUSINESS_UNIT.EDIT_BUSINESS_UNIT,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  // Get business units and divisions from Redux store using the enhanced hook
  const {
    businessUnitsWithDivisions,
    fetchBusinessUnitsWithDivisionsApiStatus,
    deleteBusinessUnitApiStatus,
    createBusinessUnitApiStatus,
    updateBusinessUnitApiStatus,
    loadBusinessUnitsWithDivisions,

    createNewBusinessUnit,
    updateBusinessUnit,
    clearAllBusinessUnitApiMessages,
  } = useBusinessUnit();

  const {
    createDivision,
    updateDivision,
    createDivisionApiStatus,
    updateDivisionApiStatus,
    deleteDivisionApiStatus,
    clearAllDivisionApiMessages,
  } = useDivision();

  const {
    searchText,
    setSearchText: setSearchTextInternal,
    setQuery,
    query,
    filteredItems,
    filterOptions,
    setFilterOptions,
  } = useGenericFilter<BusinessUnitWithDivisions>(
    businessUnitsWithDivisions,
    getItemValue,
    getSearchableFields,
    initialFilters,
    undefined, // nestedArrayConfig
    queryFilters.searchText || '', // Use persisted search text from queryFilters
  );

  // Temporary query state for filter modal (only applied on "Apply" click)
  const [tempQuery, setTempQuery] = useState(query);

  // Use custom hook for PropertyFilter persistence (search text + query tokens sync)
  const setSearchText = usePropertyFilterPersistence(
    setSearchTextInternal,
    setQueryFilters,
    queryFilters,
    setQuery,
  );

  // Local pagination
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: PaginationConfig.BUSINESSUNIT_AND_DIVISION_MAX_RESULT,
    });
  const [showFilterModal, setShowFilterModal] = useState(false);
  // State for business unit form
  const [isBusinessUnitModalVisible, setIsBusinessUnitModalVisible] = useState(false);
  // State for division form
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string | null>(null);

  useEffect(() => {
    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [
          permissionConfig.BUSINESS_UNIT.EDIT_BUSINESS_UNIT,
          permissionConfig.DIVISION.EDIT_DIVISION,
        ],
        divisions: getDivisions(),
      });
    }
    setDefaultExpandView(getPersona() !== Persona.PLATFORM_ADMIN);
    loadData();
  }, [currentUser]);

  const loadData = (nextToken?: string, maxResults?: number) => {
    const businessUnitId =
      getPersona() === Persona.PLATFORM_ADMIN ? undefined : getUserBusinessUnitId();

    loadBusinessUnitsWithDivisions({
      programId: getUserProgramId(),
      maxResults: maxResults,
      nextToken: nextToken,
      isDeleted: false,
      businessUnitId,
    });
  };
  useEffect(() => {
    if (
      updateBusinessUnitApiStatus &&
      updateBusinessUnitApiStatus.businessUnitId &&
      updateBusinessUnitApiStatus.isSuccess &&
      !updateBusinessUnitApiStatus.isLoading
    ) {
      // This will trigger the onEditRow callback with null
      // Find the index of this business unit in the data array
      cancelEditing(
        tableData.findIndex(
          (item) => item.businessUnitId === updateBusinessUnitApiStatus.businessUnitId,
        ),
      );
    }
  }, [updateBusinessUnitApiStatus]);

  // Close modal when creation is successful
  useEffect(() => {
    if (createBusinessUnitApiStatus.isSuccess) {
      setIsBusinessUnitModalVisible(false);
    }
  }, [createBusinessUnitApiStatus.isSuccess]);

  const columns: Column<ExpandTableData>[] = useMemo(
    () => [
      { key: 'name', header: 'Business Unit', editable: false },
      { key: 'description', header: 'Description', editable: isEditBusinessUnit() },
      { key: 'status', header: 'Status' },
      { key: 'createdBy', header: 'Created By' },
      { key: 'createdAt', header: 'Created At' },
      { key: 'lastUpdatedBy', header: 'Updated By' },
      { key: 'lastUpdatedAt', header: 'Updated At' },

      ...(isEditBusinessUnit() ? [{ key: 'action', header: 'Action' }] : []),
    ],
    [isEditBusinessUnit()],
  );

  // Handler for toggling business unit status (would connect to API in real implementation)
  const handleToggleBusinessUnitStatus = useCallback(
    (businessUnit: BusinessUnitWithDivisions) => {
      updateBusinessUnit({
        updatedBy: getUserId(),
        programId: getUserProgramId(),
        businessUnitId: businessUnit.businessUnitId,
        name: businessUnit.name,
        isActive: !businessUnit.isActive,
      });
    },
    [],
  );

  // Store business unit names , description  in a single state object
  const [businessUnitParams, setBusinessUnitParams] = useState<Record<string, string>>(
    {},
  );

  // Update a specific business unit name
  const updateBusinessUnitParamsState = useCallback((key: string, value: string) => {
    setBusinessUnitParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Initialize business unit names when data changes
  useEffect(() => {
    const initialNames: Record<string, string> = {};
    businessUnitsWithDivisions.forEach((bu: BusinessUnitWithDivisions) => {
      initialNames[getUniqueKeyforParamas('businessUnitId', bu.businessUnitId)] =
        bu.businessUnitId;
      initialNames[getUniqueKeyforParamas('name', bu.businessUnitId)] = bu.name;
    });
    setBusinessUnitParams(initialNames);
  }, [businessUnitsWithDivisions]);

  // Handler for toggling division status (would connect to API in real implementation)
  const handleToggleDivisionStatus = useCallback((division: Division) => {
    updateDivision({
      divisionName: division.divisionName,
      desc: division.desc,
      updatedBy: getUserId(),
      programId: getUserProgramId(),
      businessUnit: division.businessUnit,
      isActive: !division.isActive,
    });
  }, []);

  // Handler for editing division name (would connect to API in real implementation)
  const handleDivisionEdit = useCallback(
    async (item: any, column: any, newValue: any, businessUnitId: string) => {
      updateDivision({
        divisionName: item.divisionName,
        desc: newValue,
        updatedBy: getUserId(),
        programId: getUserProgramId(),
        businessUnit: businessUnitId,
        isActive: item.isActive,
      });
    },
    [],
  );

  const renderDivisionTable = (businessUnit: BusinessUnitWithDivisions) => {
    return (
      <Table
        submitEdit={async (item, column, newValue) =>
          handleDivisionEdit(item, column, newValue, businessUnit.businessUnitId)
        }
        columnDefinitions={[
          {
            id: 'divisionName',
            header: 'Division',
            cell: (item) => item.divisionName,
          },
          {
            id: 'description',
            header: 'Description',
            cell: (item) => item.desc,
            editConfig: isEditDivision()
              ? {
                  ariaLabel: 'Description',
                  editIconAriaLabel: 'editable',
                  errorIconAriaLabel: 'Name Error',
                  editingCell: (item, { currentValue, setValue }) => (
                    <Input
                      autoFocus={true}
                      value={currentValue ?? item.desc}
                      onChange={(event) => setValue(event.detail.value)}
                    />
                  ),
                }
              : undefined,
          },
          {
            id: 'createdBy',
            header: 'Created By',
            cell: (item) => item.createdBy || '-',
          },
          {
            id: 'createdAt',
            header: 'Created At',
            cell: (item) => (item.createdAt ? formatDateTime(item.createdAt) : '-'),
          },
          {
            id: 'lastUpdatedBy',
            header: 'Updated By',
            cell: (item) => item.lastUpdatedBy || '-',
          },
          {
            id: 'lastUpdatedAt',
            header: 'Updated At',
            cell: (item) =>
              item.lastUpdatedAt ? formatDateTime(item.lastUpdatedAt) : '-',
          },

          {
            id: 'isActive',
            header: 'Status',

            cell: (item: any) => (
              <StatusIndicator type={item.isActive ? 'success' : 'error'}>
                {item.isActive ? 'Active' : 'Inactive'}
              </StatusIndicator>
            ),
          },

          {
            id: 'action',
            header: 'Action',
            cell: (item) => (
              <ButtonDropdown
                expandToViewport={true}
                items={[
                  {
                    id: 'status',
                    text: !item.isActive ? 'Enable' : 'Disable',
                  },
                ]}
                ariaLabel="Control division"
                variant="inline-icon"
                loading={
                  (deleteDivisionApiStatus.divisionName === item.divisionName &&
                    deleteDivisionApiStatus.isLoading) ||
                  (updateDivisionApiStatus.divisionName === item.divisionName &&
                    updateDivisionApiStatus.isLoading)
                }
                onItemClick={(event) => {
                  if (event.detail.id === 'status') {
                    handleToggleDivisionStatus(item);
                  }
                }}
              />
            ),
          },
        ]}
        columnDisplay={[
          { id: 'divisionName', visible: true },
          { id: 'description', visible: true },
          { id: 'isActive', visible: true },
          { id: 'createdBy', visible: true },
          { id: 'createdAt', visible: true },
          { id: 'lastUpdatedBy', visible: true },
          { id: 'lastUpdatedAt', visible: true },

          { id: 'action', visible: isEditDivision() },
        ]}
        items={businessUnit.divisions}
        loadingText="Loading divisions"
        empty={
          <Box textAlign="center" padding="m">
            <b>No divisions</b>
            <Box padding={{ top: 's' }}>
              This business unit doesn't have any divisions.
            </Box>
          </Box>
        }
        footer={
          isEditDivision() ? (
            <Box textAlign="center">
              <Button
                data-testid="add-division-button"
                variant="normal"
                onClick={() => {
                  setSelectedBusinessUnit(businessUnit.businessUnitId);
                }}
              >
                Add Division
              </Button>
            </Box>
          ) : undefined
        }
      />
    );
  };

  const getUniqueKeyforParamas = (columnsKey: string, businessId: string) =>
    columnsKey + '-' + businessId;
  const renderEditComponent = (
    columnKey: string,
    businessUnit: BusinessUnitWithDivisions,
  ) => {
    //checking api loader
    const isLoadingSaveButton =
      (updateBusinessUnitApiStatus.businessUnitId === businessUnit.businessUnitId &&
        updateBusinessUnitApiStatus.isLoading) ||
      (createBusinessUnitApiStatus.businessUnitId === businessUnit.businessUnitId &&
        createBusinessUnitApiStatus.isLoading);

    const uKey = getUniqueKeyforParamas(columnKey, businessUnit.businessUnitId);
    return (
      <SpaceBetween size={'s'} direction="horizontal" alignItems="center">
        <Input
          value={businessUnitParams[uKey]}
          onChange={({ detail }) => updateBusinessUnitParamsState(uKey, detail.value)}
        />
        {!updateBusinessUnitApiStatus.isLoading &&
          !createBusinessUnitApiStatus.isLoading && (
            <Button
              iconName="close"
              variant="inline-icon"
              onClick={() => {
                // This will trigger the onEditRow callback with null
                // Find the index of this business unit in the data array
                cancelEditing(
                  tableData.findIndex(
                    (item) => item.businessUnitId === businessUnit.businessUnitId,
                  ),
                );
              }}
            />
          )}
        <Button
          iconName="check"
          variant="inline-icon"
          loading={isLoadingSaveButton}
          onClick={() => {
            const businessUnitId =
              businessUnitParams[
                getUniqueKeyforParamas('businessUnitId', businessUnit.businessUnitId)
              ];
            const name =
              businessUnitParams[
                getUniqueKeyforParamas('name', businessUnit.businessUnitId)
              ];

            updateBusinessUnit({
              updatedBy: getUserId(),
              programId: getUserProgramId(),
              businessUnitId: businessUnitId || businessUnit.businessUnitId,
              name: name || businessUnit.name,
              isActive: businessUnit.isActive,
            });
          }}
        />
      </SpaceBetween>
    );
  };
  // Memoized table data transformation
  const tableData: ExpandTableData[] = useMemo(() => {
    return (
      paginatedItems?.map((businessUnit) => {
        // When a business unit row is expanded, load its divisions

        return {
          businessUnitId: businessUnit.businessUnitId,
          name: businessUnit.name,
          description: businessUnit.description || '-',
          lastUpdatedBy: businessUnit.lastUpdatedBy || '-',
          lastUpdatedAt: businessUnit.lastUpdatedAt
            ? formatDateTime(businessUnit.lastUpdatedAt)
            : '-',
          createdBy: businessUnit.createdBy || '-',
          createdAt: businessUnit.createdAt
            ? formatDateTime(businessUnit.createdAt)
            : '-',
          action: isEditBusinessUnit() ? (
            <ButtonDropdown
              data-testid="action-dropdownbutton"
              loading={
                (deleteBusinessUnitApiStatus.businessUnitId ===
                  businessUnit.businessUnitId &&
                  deleteBusinessUnitApiStatus.isLoading) ||
                (updateBusinessUnitApiStatus.businessUnitId ===
                  businessUnit.businessUnitId &&
                  updateBusinessUnitApiStatus.isLoading)
              }
              expandToViewport={true}
              items={[
                { id: 'status', text: !businessUnit.isActive ? 'Enable' : 'Disable' },
              ]}
              ariaLabel="Control business unit"
              variant="inline-icon"
              onItemClick={(event) => {
                if (event.detail.id === 'status') {
                  handleToggleBusinessUnitStatus(businessUnit);
                }
              }}
            />
          ) : undefined,
          status: (
            <StatusIndicator type={businessUnit.isActive ? 'success' : 'error'}>
              {businessUnit.isActive ? 'Active' : 'Inactive'}
            </StatusIndicator>
          ),
          expandedContent: renderDivisionTable(businessUnit),
          editComponent: (columnKey: string) => {
            if (!isEditBusinessUnit) {
              return undefined;
            }
            return renderEditComponent(columnKey, businessUnit);
          },
        };
      }) || []
    );
  }, [
    isDefaultExpendView,
    isEditBusinessUnit,
    isEditDivision,
    isEditDivision,
    businessUnitParams,
    updateBusinessUnitApiStatus,
    paginatedItems,
    deleteBusinessUnitApiStatus,
    updateDivisionApiStatus,
    deleteDivisionApiStatus,
    handleToggleBusinessUnitStatus,
    handleDivisionEdit,
    handleToggleDivisionStatus,
  ]);

  return (
    <ContentLayout>
      <ApiStatusFlashBar
        apiStatus={[
          createBusinessUnitApiStatus,
          createDivisionApiStatus,
          updateBusinessUnitApiStatus,
          updateDivisionApiStatus,
          deleteBusinessUnitApiStatus,
          deleteDivisionApiStatus,
        ]}
        clearMessages={() => {
          clearAllBusinessUnitApiMessages();
          clearAllDivisionApiMessages();
        }}
      />

      <Box margin={{ top: 's' }}>
        <Container
          header={
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {/* Refresh data button */}
                  <Button
                    data-testid="refresh-button"
                    iconName="refresh"
                    onClick={() => loadData()}
                  />
                  <ExportButton
                    data={convertToExportList(filteredItems)}
                    config={exportBUConfig}
                    onError={(error) => {
                      alert(error);
                    }}
                  />
                  {isEditBusinessUnit() && (
                    <Button
                      data-testid="add-button"
                      variant="normal"
                      onClick={() => setIsBusinessUnitModalVisible(true)}
                    >
                      Add Business Unit
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              Business & Divisions
            </Header>
          }
          footer={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={totalPages}
              onChange={({ detail }) => {
                collapseALLView();
                setCurrentPageIndex(detail.currentPageIndex);
              }}
            />
          }
        >
          <SpaceBetween size="m" direction="vertical">
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchText}
                filteringPlaceholder="Search Busniess Unit"
                filteringAriaLabel="Search Busniess Unit"
                onChange={({ detail }) => setSearchText(detail.filteringText)}
              />
              <Button
                data-testid="filter-button"
                iconName="filter"
                onClick={() => {
                  setTempQuery(query); // Initialize temp query with current query
                  setShowFilterModal(true);
                }}
              >
                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
              </Button>
            </SpaceBetween>
            {!fetchBusinessUnitsWithDivisionsApiStatus.isLoading &&
              businessUnitsWithDivisions.length > 0 && (
                <ExpandableTable
                  data={tableData}
                  columns={columns}
                  data-testid="expandable-table"
                  expandableContentKey="expandedContent"
                  defaultExpanded={isDefaultExpendView}
                />
              )}
          </SpaceBetween>

          {fetchBusinessUnitsWithDivisionsApiStatus.errorMessage && (
            <Box textAlign="center" padding="l">
              <Alert type="error">
                Failed to load business units:{' '}
                {fetchBusinessUnitsWithDivisionsApiStatus.errorMessage}
              </Alert>
            </Box>
          )}
          {fetchBusinessUnitsWithDivisionsApiStatus.isLoading && (
            <Box textAlign="center" padding="l">
              <StatusIndicator type="loading">Loading</StatusIndicator>
            </Box>
          )}

          {businessUnitsWithDivisions.length === 0 &&
            !fetchBusinessUnitsWithDivisionsApiStatus.isLoading && (
              <Box textAlign="center" padding="l">
                <StatusIndicator type="warning">No Business Units</StatusIndicator>
              </Box>
            )}
        </Container>
      </Box>

      {/* Business Unit Form Modal */}
      <BusinessUnitForm
        visible={isBusinessUnitModalVisible}
        onDismiss={() => setIsBusinessUnitModalVisible(false)}
        apiStatus={createBusinessUnitApiStatus}
        onSubmit={(data: any) => {
          createNewBusinessUnit({
            name: data.name,
            description: data.description,
            isActive: data.isActive,
            createdBy: getUserId(),
            programId: getUserProgramId(),
          });
        }}
      />

      {/* Division Form Modal */}
      <DivisionForm
        visible={selectedBusinessUnit !== null}
        onDismiss={() => setSelectedBusinessUnit(null)}
        apiStatus={createDivisionApiStatus}
        onSubmit={(data: any) => {
          createDivision({
            divisionName: data.name,
            isActive: data.isActive,
            desc: data.desc,
            createdBy: getUserId(),
            programId: getUserProgramId(),
            businessUnit: selectedBusinessUnit!,
          });
        }}
      />
      <FilterModal
        visible={showFilterModal}
        onDismiss={() => {
          setTempQuery(query); // Reset temp query to current query
          setShowFilterModal(false);
        }}
        headerText="Filter teams"
        query={tempQuery}
        onChange={({ detail }: { detail: PropertyFilterProps.Query }) => {
          setTempQuery(detail as any);
        }}
        customGroupsText={[
          {
            properties: 'Division',
            values: 'Division values',
            group: 'Division',
          },
          {
            properties: 'BusinessUnit',
            values: 'BusinessUnit values',
            group: 'BusinessUnit',
          },
        ]}
        loading={true}
        filteringProperties={getTeamsFilterProperties(
          getPersona() === Persona.PLATFORM_ADMIN,
        )}
        filteringOptions={filterOptions}
        filteringPlaceholder="Choose filters"
        countText={`${filteredItems?.length} matches`}
        onApply={() => {
          setCurrentPageIndex(1);
          setQuery(tempQuery); // Apply temp query to actual query
          setQueryFilters({ ...tempQuery, searchText: queryFilters.searchText });
          setShowFilterModal(false);
        }}
      />
    </ContentLayout>
  );
};

export default TeamsPage;
